import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismButton, PrismSelectInput } from '@mylighthouse/prism-ember';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import HotelChannelPricingEditCellPopover from 'lumen/components/hotel/channel-pricing/edit-cell-popover';
import HotelChannelPricingTablesCellsTableCellBase from 'lumen/components/hotel/channel-pricing/tables/cells/table-cell-base';
import HotelChannelPricingTablesCellsTableCellWrapper from 'lumen/components/hotel/channel-pricing/tables/cells/table-cell-wrapper';
import type {
  TdPmsTablePeriodColumnSignature,
  TdPmsTableRowSignature,
} from 'lumen/components/hotel/channel-pricing/tables/pms-table';
import type { Restriction, RestrictionRecommendation } from 'lumen/models';
import { logPmsTableRestrictionsCellDebugValues } from 'lumen/utils/channel-pricing/log-channels-pricing-cell-debug-values';
import { prepareRestrictionActionPayload } from 'lumen/utils/channel-pricing/prepare-restriction-action-payload';
import {
  RESTRICTION_MODEL_CONTEXTS,
  RESTRICTION_TYPE_NONE,
  RESTRICTION_TYPES,
  type RestrictionEditOption,
} from 'lumen/utils/constants/channel-pricing-constants';
import type { RestrictionActionValue } from 'lumen/utils/constants/restriction-actions';
import { differenceInDays } from 'lumen/utils/dates/native-js-date-utils';

import styles from './pms-table-restrictions-row-cell-content.module.css';

interface HotelChannelPricingTablesCellsPmsTableRestrictionsPmsTableRestrictionsRowCellContentSignature {
  Args: {
    column: TdPmsTablePeriodColumnSignature;
    parentCellId: string;
    row: TdPmsTableRowSignature;
  };
}

export default class HotelChannelPricingTablesCellsPmsTableRestrictionsPmsTableRestrictionsRowCellContent extends Component<HotelChannelPricingTablesCellsPmsTableRestrictionsPmsTableRestrictionsRowCellContentSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service('restrictions/action-poller')
  declare restrictionsActionPoller: Services['restrictions/action-poller'];
  @service('restrictions/data-request-service')
  declare restrictionsDataRequestService: Services['restrictions/data-request-service'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked isCellInEditMode = false;

  @tracked isPopoverOpen = false;

  @tracked selectedOptionId = '';
  popoverElement: HTMLElement | null = null;

  get formattedRestrictionRecommendationValue(): string {
    return (
      this.restrictionRecommendation?.formattedValues[0]?.formattedValue ?? ''
    );
  }

  get hasActionPopover(): boolean {
    return (
      !this.access.isPricingAssistantActionOrInputDisabled &&
      !this.args.column.isPastDay &&
      !this.isRestrictionBeingPushedForCurrentDay
    );
  }

  get hasCellRecommendation(): boolean {
    return (
      !this.args.column.isPastDay &&
      this.hasRestrictionRecommendation &&
      !this.isRestrictionBeingPushedForCurrentDay
    );
  }

  get hasInteractions(): boolean {
    return (
      !this.access.isPricingAssistantActionOrInputDisabled &&
      !this.args.column.isPastDay
    );
  }

  get hasRestriction(): boolean {
    // BE aggregates restrictions for the "Hotel" level value in the PMS table
    // It's possible that we receive a restriction object without a value, but purely a message to inform us about a certain state
    // That's why we do the extra hasRestrictionNotPresentOnLoadingRoomMessage check because we shouldn't visualise those
    return (
      this.restriction !== undefined &&
      !this.restriction.hasRestrictionNotPresentOnLoadingRoomMessage
    );
  }

  get hasRestrictionRecommendation(): boolean {
    return this.restrictionRecommendation !== undefined;
  }

  get iconColor(): 'inherit' | 'on-critical' {
    if (this.showException) {
      return 'on-critical';
    }

    return 'inherit';
  }

  get iconName(): 'circle-error-outline' | undefined {
    if (this.showException) {
      return 'circle-error-outline';
    }

    return undefined;
  }

  get isRestrictionBeingPushedForCurrentDay(): boolean {
    return Boolean(
      this.restrictionsActionPoller.datesBeingRefreshed[this.args.column.id] ||
      this.restrictionsDataRequestService.pendingDates[this.args.column.id],
    );
  }

  get isSelectedEditRestrictionSameAsCurrent(): boolean {
    if (
      !this.hasRestriction &&
      this.selectedOption?.type === RESTRICTION_TYPE_NONE
    ) {
      return true;
    }
    if (
      this.restriction?.values?.[0]?.type === this.selectedOption?.type &&
      this.restriction?.values[0]?.value === this.selectedOption?.value
    ) {
      return true;
    }
    return false;
  }

  get mainValue(): string {
    if (this.hasRestrictionRecommendation) {
      return this.formattedRestrictionRecommendationValue;
    }

    if (this.hasRestriction) {
      return this.restriction?.formattedValue ?? '';
    }

    return this.intl.t('channel_pricing.restriction.none');
  }

  get mainValueClass(): string {
    if (this.showException) {
      return '';
    }

    if (this.hasRestrictionRecommendation) {
      return styles['intelligence-highlight'];
    }

    return '';
  }

  get popoverId(): string {
    return `${this.args.parentCellId}-popover`;
  }

  get restriction(): Restriction | undefined {
    return this.args.column.restrictions[0];
  }

  @cached
  get restrictionEditOptions(): RestrictionEditOption[] {
    return [
      {
        id: RESTRICTION_TYPE_NONE,
        label: this.intl.t('channel_pricing.restriction.none'),
        type: RESTRICTION_TYPE_NONE,
        value: undefined,
      },
      {
        id: `${RESTRICTION_TYPES.MIN_LOS}2`,
        label: this.intl.t('general.los', { los: 2 }),
        type: RESTRICTION_TYPES.MIN_LOS,
        value: 2,
      },
      {
        id: `${RESTRICTION_TYPES.MIN_LOS}3`,
        label: this.intl.t('general.los', { los: 3 }),
        type: RESTRICTION_TYPES.MIN_LOS,
        value: 3,
      },
      {
        id: RESTRICTION_TYPES.CLOSED,
        label: this.intl.t('channel_pricing.restriction.closed'),
        type: RESTRICTION_TYPES.CLOSED,
        value: 1,
      },
    ];
  }

  // SMART DISTRIBUTION TODO: Currently taking first value here for now
  // Maybe later there can be multiple restriction values, but haven't gotten more BE updates yet
  get restrictionRecommendation(): RestrictionRecommendation | undefined {
    return this.args.column.restrictionsRecommendations[0];
  }

  get restrictionsRowActions(): {
    action: (event: Event) => void;
    buttonType: 'primary' | 'secondary';
    disabled?: boolean;
    icon: IconName;
    name: string;
  }[] {
    if (!this.isCellInEditMode) {
      if (this.hasRestrictionRecommendation) {
        return [
          {
            name: 'triggerEdit',
            buttonType: 'secondary',
            icon: 'edit-pencil',
            // eslint-disable-next-line @typescript-eslint/unbound-method
            action: this.toggleCellEditMode,
          },
          {
            name: 'send',
            buttonType: 'primary',
            icon: 'send',
            // eslint-disable-next-line @typescript-eslint/unbound-method
            action: this.sendRestrictionRecommendation,
          },
        ];
      }
      return [
        {
          name: 'triggerEdit',
          buttonType: 'primary',
          icon: 'edit-pencil',
          // eslint-disable-next-line @typescript-eslint/unbound-method
          action: this.toggleCellEditMode,
        },
      ];
    }

    return [
      {
        name: 'cancelEdit',
        buttonType: 'secondary',
        icon: 'cross',
        // eslint-disable-next-line @typescript-eslint/unbound-method
        action: this.closePopover,
      },
      {
        name: 'send',
        buttonType: 'primary',
        icon: 'send',
        disabled:
          this.selectedOption === undefined ||
          this.isSelectedEditRestrictionSameAsCurrent,
        // eslint-disable-next-line @typescript-eslint/unbound-method
        action: this.sendEditedRestriction,
      },
    ];
  }

  get secondaryValue(): string {
    if (this.hasRestrictionRecommendation) {
      return this.hasRestriction
        ? (this.restriction?.formattedValue ?? '')
        : this.intl.t('channel_pricing.restriction.none');
    }

    return '';
  }

  get selectedOption(): RestrictionEditOption | undefined {
    return this.restrictionEditOptions.find(
      (option) => option.id === this.selectedOptionId,
    );
  }

  get showException(): boolean {
    return (
      !this.isRestrictionBeingPushedForCurrentDay &&
      this.args.column.daysWithFailedRestrictionPush.includes(
        this.args.column.id,
      )
    );
  }

  @action
  closePopover(event?: Event): void {
    this.popoverElement?.hidePopover();

    if (event) {
      event.stopPropagation();
    }
  }

  @action
  handleCellClick(event: MouseEvent): void {
    // Staff user debugging: CMD/Meta + click to log cell state
    if (this.access.isStaffUser && event.metaKey) {
      event.stopPropagation();

      logPmsTableRestrictionsCellDebugValues(
        this.args.column.id,
        this.hasRestriction,
        this.hasRestrictionRecommendation,
        this.showException,
        this.restriction,
        this.restrictionRecommendation,
      );
    }
  }

  @action
  onChange(option: Option): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.selectedOptionId = option['id'];
  }

  @action
  onPopoverToggle(event: ToggleEvent): void {
    this.popoverElement =
      event.newState === 'open' ? (event.target as HTMLElement) : null;
    this.isPopoverOpen = event.newState === 'open';

    if (event.newState === 'open') {
      this.isCellInEditMode = false;

      if (this.hasRestrictionRecommendation) {
        this.selectedOptionId =
          this.restrictionRecommendation?.formattedValues[0]?.valueId || '';
      } else if (this.hasRestriction) {
        this.selectedOptionId =
          this.restriction?.formattedValues[0]?.valueId || '';
      } else {
        this.selectedOptionId = RESTRICTION_TYPE_NONE;
      }
    }
  }

  @action
  sendEditedRestriction(): void {
    if (!this.selectedOption) {
      console.error('No restriction selected');
      return;
    }

    if (!this.selectedOption.type) {
      console.error('No restriction type selected');
      return;
    }

    const restrictionActions = prepareRestrictionActionPayload(
      this.selectedOption.type,
      this.selectedOption.value,
      this.restriction,
    );

    if (restrictionActions !== null) {
      this.sendRestriction(restrictionActions, true);
    }
  }

  @action
  sendRestriction(
    restrictionActions: RestrictionActionValue[],
    isSentFromEditMode = false,
  ): void {
    const restriction_action = {
      from_date: this.args.column.id,
      values: restrictionActions,
      context: RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL,
    };

    void this.restrictionsDataRequestService.sendRestriction([
      restriction_action,
    ]);

    this.usageAnalytics.track('Restriction Pushed Manually', {
      eventTrigger: 'pms-table',
      product: 'smart-distribution',
      isSentFromEditMode,
      daysOffsetFromToday: differenceInDays(
        this.hotelAndMarketService.toLocalTime(this.args.column.id).toDate(),
        this.hotelAndMarketService.getLocalTime().toDate(),
      ),
    });

    this.closePopover();
  }

  @action
  sendRestrictionRecommendation(): void {
    const recommendationValues =
      this.restrictionRecommendation?.formattedValues[0];

    if (!recommendationValues) {
      console.error('No recommendation values found');
      return;
    }
    const restriction = prepareRestrictionActionPayload(
      recommendationValues.type,
      recommendationValues.value,
      this.restriction,
    );

    if (restriction !== null) {
      this.sendRestriction(restriction);
    }
  }

  @action
  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  @action
  toggleCellEditMode(event: Event): void {
    this.isCellInEditMode = !this.isCellInEditMode;
    event.stopPropagation();
  }

  <template>
    <HotelChannelPricingTablesCellsTableCellWrapper
      @hasInteractions={{this.hasInteractions}}
      @hasOpenCta={{this.isPopoverOpen}}
      @hasRecommendation={{this.hasCellRecommendation}}
      @id={{@parentCellId}}
      @showException={{this.showException}}
      class="edit-cell-popover-button"
      data-test-id="pms-table-restrictions-row-cell-content"
      popovertarget={{this.popoverId}}
      {{on "click" this.handleCellClick}}
    >
      <HotelChannelPricingTablesCellsTableCellBase
        @icon={{this.iconName}}
        @iconColor={{this.iconColor}}
        @isLoading={{this.isRestrictionBeingPushedForCurrentDay}}
        @isPriceCell={{false}}
        @main={{this.mainValue}}
        @mainValueClass={{this.mainValueClass}}
        @mainValueHeavyText={{this.hasCellRecommendation}}
        @secondary={{this.secondaryValue}}
        @showException={{this.showException}}
      />

    </HotelChannelPricingTablesCellsTableCellWrapper>

    {{#if this.hasActionPopover}}
      <HotelChannelPricingEditCellPopover
        @onPopoverToggle={{this.onPopoverToggle}}
        @popoverId={{this.popoverId}}
      >
        <div class="flex flex-col items-center gap-200">
          {{#if this.isCellInEditMode}}
            <PrismSelectInput
              @onChange={{this.onChange}}
              @options={{this.restrictionEditOptions}}
              @unsafe_matchTriggerWidth={{true}}
              @unsafe_renderInPlace={{true}}
              @value={{this.selectedOption}}
              {{! have renderInPlace to ensure the dropdown is rendered inside the popover (for CTA click behavior)}}
              {{! won't have any effect due to the renderInPlace :'(}}
            />
          {{/if}}

          <div class="flex w-full flex-row gap-100">
            {{#each this.restrictionsRowActions as |restrictionsRowAction|}}
              <PrismButton
                @buttonType={{restrictionsRowAction.buttonType}}
                @icon={{restrictionsRowAction.icon}}
                @isDisabled={{restrictionsRowAction.disabled}}
                @onClick={{restrictionsRowAction.action}}
                @size="small"
              />
            {{/each}}
          </div>
        </div>
      </HotelChannelPricingEditCellPopover>
    {{/if}}
  </template>
}
